import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { usePrevious } from 'react-use';
import MobileModal from 'components/consumer/MobileModal';
import useModularPDPState from 'data-hooks/useModularPDPState';
import useModularPDPCart from 'data-hooks/useModularPDPCart';
import useModularPDPPhotos from 'data-hooks/useModularPDPPhotos';
import { useParams } from 'react-router-dom';
import PDPFullScreenGallery from 'components/consumer/PDP/PDPFullScreenGallery';
import { isSelectedOptionsValid } from 'components/consumer/PDP/helpers/pdpOptionUtils';
import { logInfo } from 'commons/logger';
import MobileAndTabletOnly from 'components/consumer/MobileAndTabletOnly';
import SuspenseWithLoader from 'components/consumer/SuspenseWithLoader';
import usePdpIsModularBYOProduct from 'components/consumer/PDP/state/context/usePdpIsModularBYOProduct';
import usePDPOptions from 'components/consumer/PDP/state/context/usePDPOptions';
import usePdpSelectedOptionsVariantSku from 'components/consumer/PDP/state/pdpSelectedOptionsVariantSku/usePdpSelectedOptionsVariantSku';
import IconBuildYourOwnMobile from '../../../icons/iconBuildYourOwnMobile';
import ModularItem from './ModularItem';
import { isSelectedFabricInOptions } from './utils';
const ModularAddProductsInner = ({
  marxentSelectedFabric,
  unavailableFabric,
  setHideMarxent,
  setUnavailableFabric,
  scrollToGallery
}) => {
  const pdpIsModularBYOProduct = usePdpIsModularBYOProduct();
  const [isBYOModalOpen, setBYOModalOpen] = useState(false);
  const [isSelectionInProductOptions, setIsSelectionInProductOptions] = useState({});
  const [isFullScreenGalleryOpen, setIsFullScreenGalleryOpen] = useState(false);
  const [marxentInitialised, setMarxentInitialised] = useState(false);
  const [marxent, setMarxent] = useState(undefined);
  const {
    loading: optionsLoading,
    data: optionsData
  } = usePDPOptions();
  const {
    data: {
      modularState
    }
  } = useModularPDPState();
  const previousModularBundleOrderStatus = usePrevious(modularState?.modularBundleOrderStatus);
  const idSkuMapping = useMemo(() => {
    const mapping = {};
    for (const x of modularState?.bundleProducts || []) {
      mapping[x.sku] = x.id;
    }
    return mapping;
  }, [modularState?.bundleProducts]);
  const idSkuMappingRef = useRef(idSkuMapping);
  idSkuMappingRef.current = idSkuMapping;
  const {
    slug
  } = useParams();
  const previousSlug = usePrevious(slug);
  const productsIn3dLoader = useRef([]);
  const {
    actions: {
      updateModularOrder,
      deleteModularOrder,
      rebuildOrderFromScene
    }
  } = useModularPDPCart();
  const {
    data: {
      studioPhotos,
      configurationPhotos,
      selectedPhoto,
      currentGalleryProductPhotos
    },
    actions: {
      updateGalleryProduct
    }
  } = useModularPDPPhotos();
  const isStudioGallery = selectedPhoto?.type === 'studio';
  const photos = isStudioGallery ? studioPhotos : configurationPhotos;
  const selectedPhotoIndex = Math.max(0, photos.findIndex(({
    src
  }) => src === selectedPhoto?.src));
  const finalBundleProducts = useMemo(() => {
    if (!modularState?.bundleProducts?.length || !optionsData || optionsLoading || !pdpIsModularBYOProduct || !!optionsData?.options?.length && !!optionsData?.selectedOptions && !isSelectedOptionsValid(optionsData.selectedOptions, optionsData.options)) {
      return null;
    }

    // Sort Order:
    // Armless Chair (always on top)
    // Ottoman (always at the bottom)

    // If its Slipcover products, then "Slipcovered" at the top and "Slipcover" at the bottom
    // while following the same Armless Chair and Ottoman order above within the set

    return (modularState?.bundleProducts || []).sort((a, b) => {
      const aName = a.name.toLowerCase();
      const bName = b.name.toLowerCase();
      const aType = a?.type?.name?.toLowerCase();
      const bType = b?.type?.name?.toLowerCase();
      const isA_ArmlessChair = aType?.includes('armless chair');
      const isB_ArmlessChair = bType?.includes('armless chair');
      const isA_Ottoman = aType?.includes('ottoman');
      const isB_Ottoman = bType?.includes('ottoman');
      const isA_Slipcovered = aName.includes('slipcovered');
      const isB_Slipcovered = bName.includes('slipcovered');
      const isA_Slipcover = aName.includes('slipcover');
      const isB_Slipcover = bName.includes('slipcover');
      if (isA_Slipcovered && !isB_Slipcovered) return -1;
      if (!isA_Slipcovered && isB_Slipcovered) return 1;
      if (isA_Slipcover && !isB_Slipcover) return -1;
      if (!isA_Slipcover && isB_Slipcover) return 1;
      if (isA_ArmlessChair && !isB_ArmlessChair) return -1;
      if (!isA_ArmlessChair && isB_ArmlessChair) return 1;
      if (isA_Ottoman && !isB_Ottoman) return 1;
      if (!isA_Ottoman && isB_Ottoman) return -1;
      return 0;
    });
  }, [pdpIsModularBYOProduct, modularState.bundleProducts, optionsData, optionsLoading]);
  useEffect(() => {
    if (!optionsData?.selectedOptions || !finalBundleProducts?.length) {
      return;
    }
    const {
      selectedOptions
    } = optionsData;
    const updatedIsSelValid = {};
    finalBundleProducts.forEach(bundleProduct => {
      const isValid = isSelectedFabricInOptions(bundleProduct, selectedOptions);
      updatedIsSelValid[bundleProduct.id] = isValid;
    });
    setIsSelectionInProductOptions(updatedIsSelValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalBundleProducts]);
  const handlePostAdd = useCallback(data => {
    const activeProducts = data?.payload?.activeProductsData || [];
    productsIn3dLoader.current = activeProducts;
    rebuildOrderFromScene(activeProducts, idSkuMappingRef.current);
  }, [rebuildOrderFromScene]);
  const handlePostDelete = useCallback(data => {
    const activeProducts = data?.payload?.activeProductsData || [];
    productsIn3dLoader.current = activeProducts;
    rebuildOrderFromScene(activeProducts, idSkuMappingRef.current);
    setHideMarxent(!activeProducts.length);
  }, [rebuildOrderFromScene, setHideMarxent]);
  const handleDeleteAll = useCallback(() => {
    productsIn3dLoader.current = [];
    deleteModularOrder();
    setHideMarxent(true);
  }, [deleteModularOrder, setHideMarxent]);
  useEffect(() => {
    if (!marxent) return;
    const eventsManager = marxent.getEventManager();
    eventsManager.addCallback('POST_ADD_PRODUCT', handlePostAdd);
    eventsManager.addCallback('POST_DELETE_PRODUCT', handlePostDelete);
    eventsManager.addCallback('POST_DELETE_ALL_PRODUCTS', handleDeleteAll);
  }, [marxent, handlePostAdd, handlePostDelete, handleDeleteAll]);
  useEffect(() => {
    if (isEmpty(modularState?.modularBundleOrderStatus)) {
      if (Object.keys(previousModularBundleOrderStatus || {}).length) {
        deleteModularOrder();
        productsIn3dLoader.current = [];
        if (marxent) {
          marxent.getEventManager().deleteAllProducts();
        }
        setHideMarxent(true);
      }
    }
  }, [deleteModularOrder, marxent, modularState?.modularBundleOrderStatus, previousModularBundleOrderStatus, setHideMarxent]);
  useEffect(() => {
    if (slug !== previousSlug) {
      productsIn3dLoader.current = [];
      deleteModularOrder();
    }
  }, [slug, previousSlug, deleteModularOrder]);
  useEffect(() => {
    if (!marxent) {
      return;
    }
    (async function changeFabric() {
      try {
        await marxent.getEventManager().swapAllByStepType('Cover', marxentSelectedFabric);
        setUnavailableFabric(false);
      } catch (e) {
        setUnavailableFabric(true);
        logInfo(`Fabric ${marxentSelectedFabric} Unavailable on Marxent`);
        logInfo(e);
      }
    })();
  }, [marxent, marxentSelectedFabric, setUnavailableFabric]);
  const selectedOptionsVariantSku = usePdpSelectedOptionsVariantSku();
  const onThumbnailClick = () => {
    setIsFullScreenGalleryOpen(true);
  };
  if (!finalBundleProducts) {
    return null; // TODO: Add loading state
  }
  return <>
      <MobileAndTabletOnly>
        <MobileModal isOpen={isBYOModalOpen} onClose={() => {
        setBYOModalOpen(false);
      }} className="bg-brand-dark bg-green-pattern bg-cover" isFullHeight>
          <IconBuildYourOwnMobile />
        </MobileModal>
      </MobileAndTabletOnly>

      <PDPFullScreenGallery isOpen={isFullScreenGalleryOpen} photos={currentGalleryProductPhotos} initialSelectedPhotoIndex={selectedPhotoIndex} onClose={() => {
      setIsFullScreenGalleryOpen(false);
    }} showGridView={isStudioGallery} pdpIsModularBYOProduct className="ModularAddProducts__gallery" />
      <div className="flex flex-col justify-between w-full relative">
        <div className="text-lg flex flex-row font-bold pt-8 max-[1023px]:justify-between">
          <div>Add your {modularState?.collectionTitle || ''} pieces</div>

          <MobileAndTabletOnly>
            <button type="button" className="text-sm text-brand font-bold lg:hidden" onClick={() => {
            setBYOModalOpen(true);
          }}>
              Configuration ideas
            </button>
          </MobileAndTabletOnly>
        </div>
        <div className="ModularAddProducts__products">
          {finalBundleProducts.map(product => <ModularItem selectedOptionsExtended={optionsData?.selectedOptionsExtended} selectedOptions={optionsData?.selectedOptions} options={optionsData?.options} selectedOptionsVariantSku={selectedOptionsVariantSku} product={product} modularState={modularState} updateModularOrder={updateModularOrder} onThumbnailClick={onThumbnailClick} updateGalleryProduct={updateGalleryProduct} isSelectionInProductOptions={isSelectionInProductOptions[product.id]} marxentInitialised={marxentInitialised} setMarxentInitialised={setMarxentInitialised} setHideMarxent={setHideMarxent} unavailableFabric={unavailableFabric} slug={slug} setMarxent={setMarxent} marxent={marxent} productsIn3dLoader={productsIn3dLoader} scrollToGallery={scrollToGallery} key={product.sku + (product?.orientation?.option?.sku || '')} />)}
        </div>
      </div>
    </>;
};
ModularAddProductsInner.propTypes = {
  marxentSelectedFabric: PropTypes.string,
  unavailableFabric: PropTypes.bool,
  setHideMarxent: PropTypes.func,
  setUnavailableFabric: PropTypes.func,
  scrollToGallery: PropTypes.func
};
const ModularAddProducts = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <SuspenseWithLoader><ModularAddProductsInner {...props} /></SuspenseWithLoader>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export default ModularAddProducts;